/**
 * #712: Parseo de columnas de variante desde una fila CSV.
 *
 * Cada fila CSV puede tener hasta 3 ejes de variante (eje_1/valor_1, eje_2/valor_2, eje_3/valor_3).
 * Si la fila no tiene ningún eje con valor, retorna null (producto sin variante).
 */
import type { ProductVariantDraft } from '../../domain/productDraft';

type ParsedRow = Record<string, string>;

/**
 * Extrae los datos de variante de una fila CSV.
 * Retorna null si la fila no define una variante (sin ejes/valores).
 */
export function parseVariantFromCsvRow(
  record: ParsedRow,
  parentPrice: number,
): ProductVariantDraft | null {
  const axes: Array<{ axis: string; value: string }> = [];
  for (let i = 1; i <= 3; i += 1) {
    const axis = record[`variante_eje_${i}`]?.trim() ?? '';
    const value = record[`variante_valor_${i}`]?.trim() ?? '';
    if (axis && value) {
      axes.push({ axis, value });
    } else if (axis && !value) {
      throw new Error(`variante_eje_${i} tiene eje pero falta variante_valor_${i}.`);
    } else if (!axis && value) {
      throw new Error(`variante_valor_${i} tiene valor pero falta variante_eje_${i}.`);
    }
  }

  if (axes.length === 0) return null;

  const attributes: Record<string, string> = {};
  for (const { axis, value } of axes) {
    attributes[axis] = value;
  }

  const sku = record.variante_sku?.trim() || '';
  const barcode = record.variante_codigo_barras?.trim() || '';
  const priceRaw = record.variante_precio?.trim();
  const price = priceRaw ? (Number(priceRaw) || '') : '';

  const nameSuffix = axes.map((a) => a.value).join(' · ');

  return {
    key: `csv-variant-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    id: undefined,
    nameSuffix,
    sku,
    barcode,
    price: String(price !== '' ? price : parentPrice),
    attributes,
    stockQty: '',
    imageUrl: null,
  };
}

/**
 * Agrupa filas CSV por SKU del parent.
 * Retorna un array de grupos, cada uno con el SKU y las filas que lo comparten.
 */
export type CsvProductGroup = {
  sku: string;
  rows: Array<{ rowNumber: number; record: ParsedRow }>;
};

export function groupProductRowsBySku(
  rows: Array<{ rowNumber: number; record: ParsedRow }>,
): CsvProductGroup[] {
  const groups = new Map<string, CsvProductGroup>();
  for (const row of rows) {
    const sku = row.record.sku?.trim() ?? '';
    if (!sku) continue;
    if (!groups.has(sku)) {
      groups.set(sku, { sku, rows: [] });
    }
    groups.get(sku)!.rows.push(row);
  }
  return Array.from(groups.values());
}
